import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, Plus, Copy, LogOut, Trash2, Zap, Crown, UserPlus, Loader2, ShieldCheck, Eye, X, RefreshCw, Activity, AlertTriangle } from "lucide-react";
import Navbar from "../components/Navbar";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const ROLE_BADGE = {
  owner: { label: "Owner", cls: "bg-amber-100 text-amber-700", Icon: Crown },
  editor: { label: "Editor", cls: "bg-emerald-100 text-emerald-700", Icon: ShieldCheck },
  viewer: { label: "Viewer", cls: "bg-slate-100 text-slate-600", Icon: Eye },
};

const ACTION_LABEL = {
  created: "created", deleted: "deleted", joined: "joined the workspace",
  role_changed: "changed role", removed: "removed", topped_up: "topped up the pool",
  auto_topup: "auto top-up",
};

export default function Workspaces() {
  const { user, refreshUser, switchWorkspace } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [activity, setActivity] = useState({});
  const [actFilters, setActFilters] = useState({});

  const fetchActivity = async (w, filters) => {
    const params = new URLSearchParams();
    if (filters.member) params.set("member", filters.member);
    if (filters.action) params.set("action", filters.action);
    if (filters.since) params.set("since", filters.since);
    if (filters.until) params.set("until", filters.until);
    const qs = params.toString();
    try {
      const res = await api.get(`/workspaces/${w.id}/activity${qs ? `?${qs}` : ""}`);
      setActivity((a) => ({ ...a, [w.id]: res.data }));
    } catch (e) { toast.error("Could not load activity"); }
  };

  const updateFilter = (w, key, value) => {
    const next = { ...(actFilters[w.id] || {}), [key]: value };
    setActFilters((f) => ({ ...f, [w.id]: next }));
    fetchActivity(w, next);
  };

  const clearFilters = (w) => {
    setActFilters((f) => ({ ...f, [w.id]: {} }));
    fetchActivity(w, {});
  };
  const [atEdits, setAtEdits] = useState({});

  const load = async () => {
    try { setList((await api.get("/workspaces")).data); } catch (e) { setList([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Name your workspace"); return; }
    setBusy(true);
    try {
      await api.post("/workspaces", { name });
      setName("");
      await load(); await refreshUser();
      toast.success("Workspace created");
    } catch (e) { toast.error("Could not create"); } finally { setBusy(false); }
  };

  const join = async () => {
    if (!code.trim()) { toast.error("Enter an invite code"); return; }
    setBusy(true);
    try {
      const res = await api.post("/workspaces/join", { code });
      setCode("");
      await load(); await refreshUser();
      toast.success(res.data.already_member ? `Already in ${res.data.name}` : `Joined ${res.data.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Invalid code"); } finally { setBusy(false); }
  };

  const leave = async (w) => {
    try { await api.post(`/workspaces/${w.id}/leave`); await load(); await refreshUser(); toast.success("Left workspace"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not leave"); }
  };

  const remove = async (w) => {
    try { await api.delete(`/workspaces/${w.id}`); await load(); await refreshUser(); toast.success("Workspace deleted"); }
    catch (e) { toast.error("Could not delete"); }
  };

  const setRole = async (w, memberId, role) => {
    try { await api.patch(`/workspaces/${w.id}/members/${memberId}`, { role }); await load(); toast.success(`Role set to ${role}`); }
    catch (e) { toast.error("Could not update role"); }
  };

  const kick = async (w, memberId) => {
    try { await api.delete(`/workspaces/${w.id}/members/${memberId}`); await load(); toast.success("Member removed"); }
    catch (e) { toast.error("Could not remove"); }
  };

  const topUp = async (w) => {
    try { await switchWorkspace(w.id); navigate("/buy-credits"); }
    catch (e) { toast.error("Could not open top-up"); }
  };

  const loadActivity = async (w) => {
    if (activity[w.id]) { setActivity((a) => ({ ...a, [w.id]: null })); return; }
    await fetchActivity(w, actFilters[w.id] || {});
  };

  const saveAutoTopup = async (w) => {
    const cur = atEdits[w.id] || w.auto_topup;
    try {
      await api.patch(`/workspaces/${w.id}/autotopup`, cur);
      await load(); await refreshUser();
      toast.success("Auto top-up saved");
    } catch (e) { toast.error("Could not save auto top-up"); }
  };

  const editAt = (w, patch) => setAtEdits((s) => ({ ...s, [w.id]: { ...(s[w.id] || w.auto_topup), ...patch } }));

  const copyCode = (c) => { navigator.clipboard?.writeText(c); toast.success("Invite code copied"); };

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="font-display text-3xl font-bold tracking-tight">Workspaces</h1>
        <p className="text-slate-600 mt-1">Work as a team with a shared document library and a shared credit pool. Team exports add a <span className="font-semibold">+1 credit</span> premium and draw from the shared pool (auto-topped from the owner's balance).</p>

        <div className="grid sm:grid-cols-2 gap-4 mt-6">
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><Plus className="h-4 w-4 text-indigo-600" /> Create a workspace</h3>
            <div className="mt-3 flex gap-2">
              <Input data-testid="workspace-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme Team" className="bg-white" />
              <Button data-testid="workspace-create-button" onClick={create} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full shrink-0">Create</Button>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl p-6">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2"><UserPlus className="h-4 w-4 text-indigo-600" /> Join with a code</h3>
            <div className="mt-3 flex gap-2">
              <Input data-testid="workspace-join-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="INVITE CODE" className="bg-white font-mono" />
              <Button data-testid="workspace-join-button" onClick={join} disabled={busy} variant="outline" className="rounded-full shrink-0">Join</Button>
            </div>
          </div>
        </div>

        <h2 className="font-display text-xl font-semibold mt-8 mb-3">Your workspaces</h2>
        {list === null ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>
        ) : list.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500" data-testid="workspaces-empty">
            You're not in any workspace yet. Create one or join with a code.
          </div>
        ) : (
          <div className="space-y-4" data-testid="workspaces-list">
            {list.map((w) => (
              <div key={w.id} data-testid={`workspace-${w.id}`} className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="h-9 w-9 rounded-xl bg-indigo-50 flex items-center justify-center"><Users className="h-4 w-4 text-indigo-600" /></div>
                      <div>
                        <p className="font-semibold flex items-center gap-1.5">{w.name} {w.is_owner && <Crown className="h-3.5 w-3.5 text-amber-500" title="Owner" />}</p>
                        <p className="text-xs text-slate-500">{w.members?.length || 1} member{(w.members?.length || 1) > 1 ? "s" : ""} · Owner {w.owner_name || "—"}</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right flex flex-col items-end gap-2">
                    <span className={`inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-full ${w.low ? "text-red-600 bg-red-50" : "text-amber-600 bg-amber-50"}`}>
                      <Zap className="h-3 w-3 fill-current" /> {w.credits} pool
                    </span>
                    {w.low && (
                      <span data-testid={`low-pool-${w.id}`} className="inline-flex items-center gap-1 text-xs text-red-600 font-medium">
                        <AlertTriangle className="h-3 w-3" /> Running low
                      </span>
                    )}
                    {w.is_owner && (
                      <button data-testid={`workspace-topup-${w.id}`} onClick={() => topUp(w)} className="text-xs text-indigo-600 hover:text-indigo-700 font-medium">+ Top up pool</button>
                    )}
                  </div>
                </div>

                {w.is_owner && w.invite_code && (
                  <div className="mt-4 flex items-center gap-2">
                    <span className="text-xs text-slate-500">Invite code</span>
                    <code data-testid={`workspace-code-${w.id}`} className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{w.invite_code}</code>
                    <button data-testid={`workspace-copy-${w.id}`} onClick={() => copyCode(w.invite_code)} className="text-slate-400 hover:text-indigo-600"><Copy className="h-4 w-4" /></button>
                  </div>
                )}

                <div className="mt-4 space-y-2">
                  {(w.members || []).map((m) => {
                    const rb = ROLE_BADGE[m.role] || ROLE_BADGE.editor;
                    return (
                      <div key={m.user_id} data-testid={`member-${m.user_id}`} className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2">
                        {m.picture ? <img src={m.picture} className="h-7 w-7 rounded-full" alt="" /> : <span className="h-7 w-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-xs">{(m.name || m.email || "U")[0]}</span>}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{m.name || m.email}</p>
                          <p className="text-xs text-slate-400 truncate">{m.email}</p>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${rb.cls}`}>
                          <rb.Icon className="h-3 w-3" /> {rb.label}
                        </span>
                        {w.is_owner && m.role !== "owner" && (
                          <div className="flex items-center gap-1">
                            <select
                              data-testid={`role-select-${m.user_id}`}
                              value={m.role}
                              onChange={(e) => setRole(w, m.user_id, e.target.value)}
                              className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white"
                            >
                              <option value="editor">Editor</option>
                              <option value="viewer">Viewer</option>
                            </select>
                            <button data-testid={`remove-member-${m.user_id}`} onClick={() => kick(w, m.user_id)} className="text-slate-400 hover:text-red-500 p-1"><X className="h-4 w-4" /></button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {w.is_owner && (
                  <div data-testid={`autotopup-${w.id}`} className="mt-4 border-t border-slate-100 pt-4">
                    <label className="flex items-center gap-2 text-sm font-medium">
                      <input type="checkbox" data-testid={`autotopup-enabled-${w.id}`} checked={(atEdits[w.id] || w.auto_topup).enabled} onChange={(e) => editAt(w, { enabled: e.target.checked })} className="h-4 w-4 accent-indigo-600" />
                      <RefreshCw className="h-4 w-4 text-indigo-600" /> Auto top-up from my personal credits
                    </label>
                    <p className="text-xs text-slate-500 mt-1">When the pool drops below the threshold, credits move automatically from your personal balance.</p>
                    <div className="flex items-center gap-3 mt-2 flex-wrap">
                      <div className="flex items-center gap-1"><span className="text-xs text-slate-500">Below</span>
                        <Input type="number" data-testid={`autotopup-threshold-${w.id}`} value={(atEdits[w.id] || w.auto_topup).threshold} onChange={(e) => editAt(w, { threshold: Number(e.target.value) })} className="bg-white w-20 h-8" /></div>
                      <div className="flex items-center gap-1"><span className="text-xs text-slate-500">add</span>
                        <Input type="number" data-testid={`autotopup-amount-${w.id}`} value={(atEdits[w.id] || w.auto_topup).amount} onChange={(e) => editAt(w, { amount: Number(e.target.value) })} className="bg-white w-20 h-8" /><span className="text-xs text-slate-500">credits</span></div>
                      <Button data-testid={`autotopup-save-${w.id}`} onClick={() => saveAutoTopup(w)} variant="outline" className="rounded-full h-8">Save</Button>
                    </div>
                  </div>
                )}

                <div className="mt-4 border-t border-slate-100 pt-3">
                  <button data-testid={`activity-toggle-${w.id}`} onClick={() => loadActivity(w)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-indigo-600">
                    <Activity className="h-4 w-4" /> {activity[w.id] ? "Hide activity" : "Recent activity"}
                  </button>
                  {activity[w.id] && (
                    <div data-testid={`activity-list-${w.id}`} className="mt-2">
                      <div className="flex flex-wrap items-center gap-2 mb-2" data-testid={`activity-filters-${w.id}`}>
                        <select data-testid={`activity-filter-member-${w.id}`} value={(actFilters[w.id]?.member) || ""}
                          onChange={(e) => updateFilter(w, "member", e.target.value)}
                          className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white">
                          <option value="">All members</option>
                          {(w.members || []).map((m) => <option key={m.user_id} value={m.user_id}>{m.name || m.email}</option>)}
                        </select>
                        <select data-testid={`activity-filter-action-${w.id}`} value={(actFilters[w.id]?.action) || ""}
                          onChange={(e) => updateFilter(w, "action", e.target.value)}
                          className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white">
                          <option value="">All actions</option>
                          {Object.keys(ACTION_LABEL).map((k) => <option key={k} value={k}>{ACTION_LABEL[k]}</option>)}
                        </select>
                        <input type="date" data-testid={`activity-filter-since-${w.id}`} value={(actFilters[w.id]?.since) || ""}
                          onChange={(e) => updateFilter(w, "since", e.target.value)}
                          className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white" />
                        <input type="date" data-testid={`activity-filter-until-${w.id}`} value={(actFilters[w.id]?.until) || ""}
                          onChange={(e) => updateFilter(w, "until", e.target.value)}
                          className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white" />
                        {Object.values(actFilters[w.id] || {}).some(Boolean) && (
                          <button data-testid={`activity-clear-${w.id}`} onClick={() => clearFilters(w)}
                            className="text-xs text-indigo-600 hover:underline flex items-center gap-1"><X className="h-3 w-3" /> Clear</button>
                        )}
                      </div>
                      <div className="space-y-1.5 max-h-52 overflow-auto">
                      {activity[w.id].length === 0 ? <p className="text-xs text-slate-400">No matching activity.</p> :
                        activity[w.id].map((e) => (
                          <div key={e.id} className="text-xs text-slate-600 flex items-center gap-2">
                            <span className="text-slate-400 font-mono shrink-0">{new Date(e.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                            <span><span className="font-medium">{e.actor_name}</span> {ACTION_LABEL[e.action] || e.action}{e.target ? <span className="text-slate-500"> "{e.target}"</span> : null}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-400">
                    {w.my_role === "viewer" ? "You have view-only access." : w.my_role === "editor" ? "You can create & export in this workspace." : "You own this workspace."}
                  </span>
                  {w.is_owner ? (
                    <Button data-testid={`workspace-delete-${w.id}`} onClick={() => remove(w)} variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
                      <Trash2 className="h-4 w-4 mr-1.5" /> Delete
                    </Button>
                  ) : (
                    <Button data-testid={`workspace-leave-${w.id}`} onClick={() => leave(w)} variant="outline" className="rounded-full">
                      <LogOut className="h-4 w-4 mr-1.5" /> Leave
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
